// ReportGrid.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// Import the specialized sections
import { UnmetDemandWaterSystemSection } from './UnmetDemandWaterSystemSection';
import { UnmetDemandPowerSystemSection } from './UnmetDemandPowerSystemSection';
import { UnmetDemandTransportSystemSection } from './UnmetDemandTransportSystemSection';

// Import authFetch wrapper
import { authFetch } from '../authFetch'; 

// --- Helper Components ---
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`analysis-tabpanel-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function UnmetDemandSubTabs({ analysisData, nameMap, floodData, llmSummaries }) {
  const [subTabValue, setSubTabValue] = useState(0);
  const handleSubTabChange = (event, newValue) => setSubTabValue(newValue);

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          aria-label="unmet demand system types"
          variant="fullWidth"
        >
          <Tab label="Water Supply Shortages" id="unmet-demand-subtab-0" />
          <Tab label="Electricity Supply Shortages" id="unmet-demand-subtab-1" />
          <Tab label="Social Service Supply Restrictions" id="unmet-demand-subtab-2" />
        </Tabs>
      </Paper>
      <TabPanel value={subTabValue} index={0}>
        <UnmetDemandWaterSystemSection analysisData={analysisData} nameMap={nameMap} floodData={floodData} llmSummaries={llmSummaries} />
      </TabPanel>
      <TabPanel value={subTabValue} index={1}>
        <UnmetDemandPowerSystemSection analysisData={analysisData} nameMap={nameMap} floodData={floodData} llmSummaries={llmSummaries} />
      </TabPanel>
      <TabPanel value={subTabValue} index={2}>
        <TransportAccessSubTabs analysisData={analysisData} nameMap={nameMap} floodData={floodData} llmSummaries={llmSummaries} />
      </TabPanel>
    </Box>
  );
}

function TransportAccessSubTabs({ analysisData, nameMap, floodData, llmSummaries }) {
  const [transportSubTabValue, setTransportSubTabValue] = useState(0);
  const handleTransportSubTabChange = (event, newValue) => setTransportSubTabValue(newValue);

  const transportSystems = [
    { key: 'health', label: 'Healthcare' },
    { key: 'police', label: 'Police' },
    { key: 'fire', label: 'Fire Station' },
    { key: 'military', label: 'Military' },
    { key: 'pharmacy', label: 'Pharmacy' },
    { key: 'food', label: 'Food' },
    { key: 'train', label: 'Train Station' },
    { key: 'school', label: 'School' },
  ];

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={transportSubTabValue}
          onChange={handleTransportSubTabChange}
          aria-label="transport access system types"
          variant="scrollable"
          scrollButtons="auto"
        >
          {transportSystems.map((system) => (
            <Tab key={system.key} label={system.label} />
          ))}
        </Tabs>
      </Paper>
      {transportSystems.map((system, index) => (
        <TabPanel key={system.key} value={transportSubTabValue} index={index}>
          <UnmetDemandTransportSystemSection
            analysisData={analysisData}
            nameMap={nameMap}
            systemLabel={system.label}
            systemKey={system.key}
            floodData={floodData}
            llmSummaries={llmSummaries}
          />
        </TabPanel>
      ))}
    </Box>
  );
}

// --- Main Report Component ---
export default function ReportGrid() {
  const [analysisData, setAnalysisData] = useState(null);
  const [nameMap, setNameMap] = useState(null);
  const [floodData, setFloodData] = useState(null);
  const [llmSummaries, setLlmSummaries] = useState(null); // State for LLM summaries
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [analysisResponse, nameMapResponse, floodResponse, llmResponse] = await Promise.all([
          authFetch('/api/realtime-summary/'),
          authFetch('/api/name-map/'),
          authFetch('/api/flood-data/'),
          authFetch('/api/llm-summary/'), // Fetch LLM summaries
        ]);

        const analysis = await analysisResponse.json();
        const names = await nameMapResponse.json();
        const floodApiResponse = await floodResponse.json();
        const floodGeoJson = floodApiResponse.data;
        const llmData = await llmResponse.json(); // Parse LLM summaries

        setAnalysisData(analysis);
        setNameMap(names);
        setFloodData(floodGeoJson);
        setLlmSummaries(llmData); // Set LLM summaries to state
      } catch (e) {
        console.error('Failed to fetch analysis, flood, or LLM data:', e);
        setError(`Failed to load analysis summary, flood risk data, or AI insights. ${e.message || e}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading analysis results...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!analysisData || Object.keys(analysisData).length === 0) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Alert severity="warning">No analysis data available.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box className="w-full">
        <Typography component="h1" variant="h5" sx={{ my: 3 }}>
          Simulated Systemic Infrastructure Risks
        </Typography>
        <UnmetDemandSubTabs analysisData={analysisData} nameMap={nameMap} floodData={floodData} llmSummaries={llmSummaries} />
      </Box>
    </Container>
  );
}